import { FormInputSearch, FormSelect, Tooltip, toast } from '@systemeio/ui'
import { getServerSideFlagsProps } from 'growthbook/server'
import { FlagsPropsType, GrowthbookFlagsEnum } from 'growthbook/types'
import CourseCreateModal from 'modules/courses/components/create-modal'
import TabMenu from 'modules/courses/components/tab-menu'
import { coursesDependencies } from 'modules/courses/constants/courses-dependencies'
import { CoursesLoco } from 'modules/courses/constants/courses-loco'
import {
  activateCourse,
  duplicateCourse,
  removeCourse,
} from 'modules/courses/courses/api/courses-api'
import { useCourses } from 'modules/courses/courses/hooks/use-courses'
import { CourseFilterInterface } from 'modules/courses/courses/types/course-filter-interface'
import {
  CourseDuplicationStatusEnum,
  CourseEditorTypeEnum,
  CourseInterface,
} from 'modules/courses/courses/types/course-interface'
import { getCourseCurriculumUrl } from 'modules/courses/courses/utils/get-course-curriculum-url'
import { getCourseSettingsUrl } from 'modules/courses/courses/utils/get-course-settings-url'
import { StatusDataInterface } from 'modules/funnels/types/funnel-filter-interface'
import { ShareResourceEnum } from 'modules/share/enum/share-resource-enum'
import { getShareUrl } from 'modules/share/utils/get-share-url'
import { NextPage } from 'next'
import React from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import LinkShareModal from 'shared/components/link-share-modal'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { BooleanStatusEnum } from 'shared/enums/status-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import {
  getBaseServerSideProps,
  getCombinedServerSideProps,
} from 'shared/utils/get-base-server-side-props'
import { CheckCircleIcon, TimeCircleIcon } from '@systemeio/ui'

const defaultFilters: CourseFilterInterface = { search: '', status: undefined }

const Course: NextPage<FlagsPropsType> = ({ flags }) => {
  const { t } = useLocoTranslation()
  const { setPagination, pagination, setLimit, limit, filter, setFilter } =
    useFilterPagination(defaultFilters)
  const { courses, mutate, counter } = useCourses({ limit, pagination, filter })

  const { revalidateModules } = useRevalidateModules()

  const getStatusData = (): StatusDataInterface[] =>
    Object.keys(BooleanStatusEnum).map(key => {
      const status = key as keyof typeof BooleanStatusEnum
      return { id: status, caption: t(BooleanStatusEnum[status]) }
    })

  const columns: ColumnDefinitionInterface<CourseInterface>[] = [
    {
      key: 'name',
      header: t('global.title'),
      widthPercent: 65,
      onRender: course => {
        const link =
          course.duplicationStatus === CourseDuplicationStatusEnum.Pending ? (
            course.name
          ) : (
            <LinkWithoutPrefetch
              className="text-brand-primary hover:text-text-secondary ring-brand-primary rounded-xs w-full outline-hidden ring-offset-1 focus-visible:ring-2"
              href={getCourseCurriculumUrl({ courseId: course.id, courseType: course.courseType })}
            >
              <span data-test-id="course-name-link">{course.name}</span>
            </LinkWithoutPrefetch>
          )

        return <div className="truncate">{link}</div>
      },
    },
    {
      key: 'type',
      header: t('global.type'),
      widthPercent: 20,
      onRender: course => {
        return (
          <span className="truncate">
            {course.courseType === CourseEditorTypeEnum.ClassicCourse
              ? t('dashboard.store.advanced_course.title')
              : t('dashboard.light_course.title')}
          </span>
        )
      },
    },
    {
      key: 'active',
      header: t('global.status'),
      widthPercent: 15,
      onRender: course => {
        const label =
          course.duplicationStatus === CourseDuplicationStatusEnum.Pending
            ? t('dashboard.course.duplication_status_pending_description')
            : course.active
            ? t('global.active')
            : t('global.inactive')

        const icon =
          course.duplicationStatus === CourseDuplicationStatusEnum.Pending ? (
            <TimeCircleIcon
              className={`fill-yellow h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]`}
              data-test-id="course-status-icon-pending"
            />
          ) : (
            <CheckCircleIcon
              className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${
                course.active && 'fill-green'
              }`}
              data-test-id={`course-status-icon-${course.active ? 'active' : 'inactive'}`}
            />
          )

        return (
          <div className="flex items-center lg:ml-4">
            <Tooltip
              label={<span data-test-id="course-status-tooltip">{label}</span>}
              mode="hover"
              key="status-tooltip"
            >
              {icon}
            </Tooltip>
          </div>
        )
      },
    },
  ]

  const onDuplicateCourse = async (course: CourseInterface) => {
    const { duplicationStatus } = (await duplicateCourse(course.id)).data

    if (duplicationStatus === CourseDuplicationStatusEnum.Pending) {
      toast.success(t('dashboard.course.course_duplicated'))
    } else {
      toast.success(t('dashboard.course.course_duplicating'))
    }

    await revalidateModules(coursesDependencies, true)
  }

  const onActivateCourse = async (course: CourseInterface) => {
    const update = course.active ? 'deactivate' : 'activate'
    await activateCourse(update, course.id)
    await mutate(data => {
      if (data) {
        return {
          items: data.items.map(item => {
            if (item.id === course.id) item = { ...item, active: !item.active }
            return item
          }),
          hasMore: data.hasMore,
        }
      }
    }, false)
    await revalidateModules(coursesDependencies)
  }

  const onRemoveCourse = async (course: CourseInterface) => {
    await removeCourse(course.id)
    await revalidateModules(
      [
        ...coursesDependencies,
        RequestModuleEnum.MembershipLightCourseCurriculum,
        RequestModuleEnum.MembershipCourseCurriculum,
        RequestModuleEnum.MembershipCourseLecture,
      ],
      true,
    )
  }

  const handleSave = async () => await revalidateModules([RequestModuleEnum.MembershipCourse], true)

  return (
    <>
      <TabMenu showAssigments={flags[GrowthbookFlagsEnum.Assignment]} />
      <ListingLayout
        data={courses}
        counter={counter}
        title={t('dashboard.courses.title_plural')}
        filterData={{
          filterer: { filter, setFilter, defaultFilters },
          paginator: { pagination, setPagination },
          limiter: { limit, setLimit },
          testAttributePostfix: 'filter-btn',
          filterable: [
            {
              field: 'search',
              onRender: (value, onChange) => (
                <FormInputSearch
                  key={'courses-search-filter'}
                  onClear={() => onChange('')}
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  testAttributePostfix={'search-input'}
                />
              ),
            },
            {
              field: 'status',
              onRender: (value, onChange) => (
                <FormSelect
                  key={'courses-status-filter'}
                  data={getStatusData()}
                  onChange={data => onChange(data)}
                  value={value}
                />
              ),
            },
          ],
        }}
        customHeaderElement={<CourseCreateModal onSave={handleSave} />}
        type={'compact'}
      >
        <Table
          caption={t('dashboard.courses.title')}
          data={courses}
          columns={columns}
          limiter={{ limit, setLimit }}
          paginator={{ pagination, setPagination }}
          testAttributePostfix="courses-table"
          actions={{
            openLink: [
              {
                name: 'view-course',
                caption: t('dashboard.actions.title.open'),
                setHref: course => course.loginLinkUrl,
                openInNew: true,
                shouldRender: course =>
                  course.duplicationStatus !== CourseDuplicationStatusEnum.Pending,
              },
              {
                name: 'curriculum',
                setHref: course =>
                  getCourseCurriculumUrl({ courseId: course.id, courseType: course.courseType }),
                caption: t('dashboard.course.curriculum'),
                openInNew: false,
                isNext: true,
                shouldRender: course =>
                  course.duplicationStatus !== CourseDuplicationStatusEnum.Pending,
              },
              {
                name: 'settings',
                setHref: course =>
                  getCourseSettingsUrl({ courseId: course.id, courseType: course.courseType }),
                caption: t('dashboard.actions.title.settings'),
                openInNew: false,
                isNext: true,
                shouldRender: course =>
                  course.duplicationStatus !== CourseDuplicationStatusEnum.Pending,
              },
            ],
            custom: [
              {
                name: 'share-course',
                caption: t('dashboard.actions.title.share'),
                onActionRender: (opened, onClose, course, key) => (
                  <LinkShareModal
                    opened={opened}
                    link={getShareUrl(ShareResourceEnum.Course, course.hash)}
                    title={t('dashboard.course.share_course')}
                    label={t('dashboard.course.share_course_text')}
                    onClose={onClose}
                    key={key}
                  />
                ),
                shouldRender: course =>
                  course.duplicationStatus !== CourseDuplicationStatusEnum.Pending &&
                  course.courseType === CourseEditorTypeEnum.ClassicCourse,
              },
              {
                name: 'duplicate-course',
                caption: t('dashboard.actions.title.duplicate'),
                onActionRender: (opened, onClose, course, key) => (
                  <ConfirmationModal
                    confirmationText={t('dashboard.course.confirm_duplicate')}
                    key={key}
                    opened={opened}
                    onClose={onClose}
                    onConfirm={() => onDuplicateCourse(course)}
                  />
                ),
                shouldRender: course =>
                  course.duplicationStatus !== CourseDuplicationStatusEnum.Pending &&
                  course.courseType === CourseEditorTypeEnum.ClassicCourse,
              },
            ],
            activate: {
              onChange: course => onActivateCourse(course),
              confirmationText: 'dashboard.course.activate_confirmation_course',
              shouldRender: course =>
                course.duplicationStatus !== CourseDuplicationStatusEnum.Pending,
            },
            remove: {
              onChange: course => onRemoveCourse(course),
              shouldRender: course =>
                course.duplicationStatus !== CourseDuplicationStatusEnum.Pending,
            },
          }}
        />
      </ListingLayout>
    </>
  )
}

export const getServerSideProps = getCombinedServerSideProps(
  getBaseServerSideProps(CoursesLoco, 'header.top_menu.courses'),
  getServerSideFlagsProps(),
)

export default Course
